import * as ProtoRead from "../../gen/orders/v1/orders_read_pb.js";
import * as ProtoWrite from "../../gen/orders/v1/orders_pb.js";
import { createClient, type Client } from "@connectrpc/connect";
import * as v from "valibot";
import { parse } from "../../shared/validation.js";
import { type SubaccountResolver, resolveAccountScopedInput } from "../subaccount-resolver.js";
import { removeUndefined } from "../../utils/remove-undefined.js";
import {
    toConnectCallOptions,
    type PolyesterMutationOptions,
    type PolyesterRequestOptions,
} from "../../shared/request-options.js";
import type { PolyesterRealtime } from "../../realtime/types.js";
import { connectReadyGatedProtoChannel } from "../../realtime/ready-gated-subscription.js";
import type { BaseSubscribeInput } from "../../shared/types.js";
import type { SdkScales } from "../../shared/decimal-surface.js";
import { toPolyesterError } from "../../shared/connect-error-mapping.js";
import { loadErrorDetailDecoders } from "../../shared/error-detail.js";
import { ConfigurationError, PolyesterError } from "../../shared/errors.js";
import type { AuthApiTransports } from "../../shared/transports.js";
import { formatConnectError, isResourceNotFoundError } from "../../utils/errors.js";
import {
    OpenOrdersInputSchema,
    OrderHistoryInputSchema,
    type NewOrderInput,
    createNewOrderInputSchema,
    CancelOrderInputSchema,
    CancelOrderResultSchema,
    type CancelOrderResult,
    CancelAllOrdersInputSchema,
    CancelAllOrdersResponseSchema,
    type CancelAllOrdersResponse,
    type Order,
    GetOrderDetailsInputSchema,
    createCreateOrderResultSchema,
    createPreviewOrderResultSchema,
    type PreviewOrderResult,
    type ModifyOrderInput,
    assertKnownModifyOrderInputKeys,
    createModifyOrderInputSchema,
    ModifyOrderResultSchema,
    type CreateOrderResult,
    type ModifyOrderResult,
    type OrderDetails,
    createOrderSchema,
    createOrderDetailsSchema,
    CancelAllAfterInputSchema,
    CancelAllAfterResultSchema,
    type CancelAllAfterInput,
    type CancelAllAfterResult,
    BatchCancelOrdersInputSchema,
    BatchCancelOrdersResultSchema,
    type BatchCancelOrdersInput,
    type BatchCancelOrdersResult,
    createBatchCreateOrdersResultSchema,
    type BatchCreateOrdersInput,
    type BatchCreateOrdersResult,
    BatchReplaceOrdersResultSchema,
    type BatchReplaceOrdersInput,
    type BatchReplaceOrdersResult,
    GetBatchReplaceStatusInputSchema,
    GetBatchReplaceStatusResultSchema,
    type GetBatchReplaceStatusInput,
    type GetBatchReplaceStatusResult,
    assertKnownBatchReplaceOrderItemInputKeys,
    createBatchCreateOrdersInputSchema,
    createBatchReplaceOrdersInputSchema,
} from "./orders.schemas.js";

function hasKnownOrderSymbol(scales: SdkScales, order: { symbolId: number }): boolean {
    try {
        scales.baseQty(order.symbolId);
        return true;
    } catch {
        return false;
    }
}

const MISCLASSIFIED_ORDER_NOT_FOUND_MESSAGE = "order not found";

function isOrderNotFoundError(error: unknown): boolean {
    const mapped = toPolyesterError(error);
    return (
        isResourceNotFoundError(error) ||
        (mapped instanceof PolyesterError &&
            mapped.detail?.service === "orders" &&
            mapped.detail.code === "NOT_FOUND") ||
        formatConnectError(error, "").toLowerCase() === MISCLASSIFIED_ORDER_NOT_FOUND_MESSAGE
    );
}

interface SubscribeOrdersInput extends BaseSubscribeInput<Order> {
    accountId: string;
}

function createMutationRequestId(): string {
    const crypto = globalThis.crypto;
    if (crypto?.randomUUID) return crypto.randomUUID();

    if (crypto?.getRandomValues) {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    }

    throw new ConfigurationError(
        "Secure request ID generation requires crypto.randomUUID or crypto.getRandomValues.",
    );
}

function assertBatchResultCount(operation: string, requested: number, returned: number): void {
    if (requested !== returned) {
        throw new Error(
            `${operation} returned ${returned} results for ${requested} requested items.`,
        );
    }
}

/**
 * Manages account-scoped spot orders across read, write, and realtime order update surfaces.
 */
export class OrdersService {
    #readClient: Client<typeof ProtoRead.OrdersReadService>;
    #writeClient: Client<typeof ProtoWrite.OrdersService>;
    #realtime: PolyesterRealtime;
    #resolver?: SubaccountResolver;
    #scales: SdkScales;
    #orderSchema: ReturnType<typeof createOrderSchema>;
    #orderDetailsSchema: ReturnType<typeof createOrderDetailsSchema>;
    #newOrderInputSchema: ReturnType<typeof createNewOrderInputSchema>;
    #modifyOrderInputSchema: ReturnType<typeof createModifyOrderInputSchema>;
    #batchCreateOrdersInputSchema: ReturnType<typeof createBatchCreateOrdersInputSchema>;

    constructor(
        transports: AuthApiTransports,
        realtime: PolyesterRealtime,
        resolver: SubaccountResolver | undefined,
        scales: SdkScales,
    ) {
        this.#readClient = createClient(ProtoRead.OrdersReadService, transports.authApi);
        this.#writeClient = createClient(ProtoWrite.OrdersService, transports.authApi);
        this.#realtime = realtime;
        this.#resolver = resolver;
        this.#scales = scales;
        this.#orderSchema = createOrderSchema(scales);
        this.#orderDetailsSchema = createOrderDetailsSchema(scales);
        this.#newOrderInputSchema = createNewOrderInputSchema(scales);
        this.#modifyOrderInputSchema = createModifyOrderInputSchema(scales);
        this.#batchCreateOrdersInputSchema = createBatchCreateOrdersInputSchema(scales);
    }

    /**
     * Returns open orders for the resolved root account or subaccount, with optional symbol IDs, trigger ID, side, pagination, and attached-risk inclusion filters. Results are paginated with a server-determined page size: a single call is not the full set of open orders — keep calling with the returned nextPageToken until it is empty.
     */
    async listOpen(
        input: v.InferInput<typeof OpenOrdersInputSchema> = {},
        options?: PolyesterRequestOptions,
    ): Promise<{ orders: Order[]; nextPageToken: string }> {
        await this.#scales.ready();
        const resolved = resolveAccountScopedInput(input, this.#resolver);
        const validatedInput = parse(OpenOrdersInputSchema, resolved);
        const res = await this.#readClient.getOpenOrders(
            removeUndefined(validatedInput),
            toConnectCallOptions(options),
        );
        return {
            orders: parse(
                v.array(this.#orderSchema),
                res.orders.filter((order) => hasKnownOrderSymbol(this.#scales, order)),
            ),
            nextPageToken: res.nextPageToken,
        };
    }

    /**
     * Returns historical orders for the resolved account scope, supporting symbol IDs, trigger ID, side, status, time range, pagination, and attached-risk filters. Results are paginated with the backend nextPageToken.
     */
    async listHistory(
        input: v.InferInput<typeof OrderHistoryInputSchema> = {},
        options?: PolyesterRequestOptions,
    ): Promise<{ orders: Order[]; nextPageToken: string }> {
        await this.#scales.ready();
        const resolved = resolveAccountScopedInput(input, this.#resolver);
        const validatedInput = parse(OrderHistoryInputSchema, resolved);
        const res = await this.#readClient.getOrderHistory(
            removeUndefined(validatedInput),
            toConnectCallOptions(options),
        );
        return {
            orders: parse(
                v.array(this.#orderSchema),
                res.orders.filter((order) => hasKnownOrderSymbol(this.#scales, order)),
            ),
            nextPageToken: res.nextPageToken,
        };
    }

    /**
     * Evaluates one complete order intent against current market, policy, risk, and balance state without creating an order, reserving funds, or claiming its client order ID.
     */
    async preview(
        input: NewOrderInput,
        options?: PolyesterRequestOptions,
    ): Promise<PreviewOrderResult> {
        await this.#scales.ready();
        const resolved = resolveAccountScopedInput(input, this.#resolver);
        const request = parse(this.#newOrderInputSchema, resolved);
        const response = await this.#writeClient.previewOrder(
            removeUndefined(request),
            toConnectCallOptions(options),
        );
        return parse(
            createPreviewOrderResultSchema(this.#scales, request.order.symbolId),
            response,
        );
    }

    /**
     * Places a spot order with an explicit market-IOC, limit-GTC, limit-IOC, or limit-FOK execution policy and optional attached risk controls. clientOrderId is an account-scoped correlation, lookup, and cancellation identifier. Reuse while retained returns CONFLICT_DUPLICATE_CLIENT_ORDER_ID, including after rejection or completion; it does not replay the earlier result. After an ambiguous timeout, reconcile through get using clientOrderId; an immediate lookup miss does not prove admission failed. Without clientOrderId, the outcome may remain unknown.
     */
    async create(
        input: NewOrderInput,
        options?: PolyesterMutationOptions,
    ): Promise<CreateOrderResult> {
        await this.#scales.ready();
        const resolved = resolveAccountScopedInput(input, this.#resolver);
        const validatedInput = parse(this.#newOrderInputSchema, resolved);
        const requestPayload = removeUndefined(validatedInput);
        const res = await this.#writeClient.createOrder(
            requestPayload,
            toConnectCallOptions(options),
        );
        return parse(
            createCreateOrderResultSchema(this.#scales, validatedInput.order.symbolId),
            res,
        );
    }

    /**
     * Places 1–20 spot orders in one best-effort request. Results preserve item order and report admission as accepted or rejected; accepted orders still require lifecycle reconciliation. requestId is the idempotency boundary for the ordered batch and is generated when omitted; supply and reuse it when an ambiguous batch may be retried. The same payload and account-scoped requestId replay the original results and timestamp within 15 minutes; a different payload during that window returns CONFLICT_IDEMPOTENCY_KEY_REUSE. Per-item clientOrderId values are optional correlation identifiers and must be unique when supplied.
     */
    async batchCreate(
        input: BatchCreateOrdersInput,
        options?: PolyesterMutationOptions,
    ): Promise<BatchCreateOrdersResult> {
        await this.#scales.ready();
        const resolved = {
            ...resolveAccountScopedInput(input, this.#resolver),
            requestId: input.requestId ?? createMutationRequestId(),
        };
        const request = parse(this.#batchCreateOrdersInputSchema, resolved);
        const response = await this.#writeClient.batchCreateOrders(
            removeUndefined(request),
            toConnectCallOptions(options),
        );
        const result = parse(
            createBatchCreateOrdersResultSchema(
                this.#scales,
                request.items.map((item) => item.symbolId),
            ),
            response,
        );
        assertBatchResultCount("batchCreate", input.items.length, result.results.length);
        return result;
    }

    /**
     * Requests cancellation of one order in the resolved account scope by order id or client order id, with optional symbol routing. The response acknowledges the cancellation request, not the order's final lifecycle state; reconcile through order reads or realtime before releasing local state. A missing target remains a {@link ResourceNotFoundError}; callers performing desired-state cleanup may treat that error as success when the order could already have filled or left the book.
     */
    async cancel(
        input: v.InferInput<typeof CancelOrderInputSchema>,
        options?: PolyesterMutationOptions,
    ): Promise<CancelOrderResult> {
        const resolved = resolveAccountScopedInput(input, this.#resolver);

        const validated = parse(CancelOrderInputSchema, resolved);

        const res = await this.#writeClient.cancelOrder(
            {
                key: validated.key,
                symbolId: validated.symbolId,
                subaccountId: validated.subaccountId,
            },
            toConnectCallOptions(options),
        );
        return parse(CancelOrderResultSchema, res);
    }

    /**
     * Cancels 1–50 explicit orders in one best-effort request. Results preserve item order and acknowledge cancellation admission rather than final order state. Supply a stable requestId when an ambiguous batch may be retried.
     */
    async batchCancel(
        input: BatchCancelOrdersInput,
        options?: PolyesterMutationOptions,
    ): Promise<BatchCancelOrdersResult> {
        const resolved = {
            ...resolveAccountScopedInput(input, this.#resolver),
            requestId: input.requestId ?? createMutationRequestId(),
        };
        const request = parse(BatchCancelOrdersInputSchema, resolved);
        const response = await this.#writeClient.batchCancelOrders(
            removeUndefined(request),
            toConnectCallOptions(options),
        );
        const result = parse(BatchCancelOrdersResultSchema, response);
        assertBatchResultCount("batchCancel", input.items.length, result.results.length);
        return result;
    }

    /**
     * Applies a price, quantity, client id, or attached-risk patch to one open order. The symbol ID routes policy checks and quantity scaling. A requestId is generated when omitted; provide a stable value when retrying the same logical modification.
     */
    async modify(
        input: ModifyOrderInput,
        options?: PolyesterMutationOptions,
    ): Promise<ModifyOrderResult> {
        await this.#scales.ready();
        const resolved = {
            ...resolveAccountScopedInput(input, this.#resolver),
            requestId: input.requestId ?? createMutationRequestId(),
        };
        assertKnownModifyOrderInputKeys(resolved);
        const validated = parse(this.#modifyOrderInputSchema, resolved);
        const res = await this.#writeClient.modifyOrder(
            removeUndefined(validated),
            toConnectCallOptions(options),
        );
        return parse(ModifyOrderResultSchema, res);
    }

    /**
     * Replaces 1–50 same-symbol orders and returns an index-stable durable admission receipt. Reuse requestId only when retrying the same logical batch; use the returned batchRequestId for later status reads. An AMENDED actionTaken is cancel-only with no successor; keep tracking oldOrderId until its terminal state is confirmed.
     */
    async batchReplace(
        input: BatchReplaceOrdersInput,
        options?: PolyesterMutationOptions,
    ): Promise<BatchReplaceOrdersResult> {
        await this.#scales.ready();
        for (const item of input.items) {
            assertKnownBatchReplaceOrderItemInputKeys(item);
        }
        const resolved = {
            ...resolveAccountScopedInput(input, this.#resolver),
            requestId: input.requestId ?? createMutationRequestId(),
        };
        const request = parse(createBatchReplaceOrdersInputSchema(this.#scales), resolved);
        const response = await this.#writeClient.batchReplaceOrders(
            removeUndefined(request),
            toConnectCallOptions(options),
        );
        const result = parse(BatchReplaceOrdersResultSchema, response);
        assertBatchResultCount("batchReplace", input.items.length, result.results.length);
        return result;
    }

    /**
     * Reads the durable per-item execution status for a batch replacement receipt. For cancel-only AMENDED items, orderStatus describes oldOrderId and replacementOrderId is undefined.
     */
    async getBatchReplaceStatus(
        input: GetBatchReplaceStatusInput,
        options?: PolyesterRequestOptions,
    ): Promise<GetBatchReplaceStatusResult> {
        const resolved = resolveAccountScopedInput(input, this.#resolver);
        const request = parse(GetBatchReplaceStatusInputSchema, resolved);
        const response = await this.#readClient.getBatchReplaceStatus(
            removeUndefined(request),
            toConnectCallOptions(options),
        );
        return parse(GetBatchReplaceStatusResultSchema, response);
    }

    /**
     * Cancels all matching open orders for the resolved account scope, optionally narrowed by up to 100 symbol IDs and side, with dry-run preview. A requestId is generated when omitted; reuse it with the same criteria for retries. Completed results are retained for at least two minutes; use a fresh key for each new cancellation.
     */
    async cancelAll(
        input: v.InferInput<typeof CancelAllOrdersInputSchema>,
        options?: PolyesterMutationOptions,
    ): Promise<CancelAllOrdersResponse> {
        const resolved = {
            ...resolveAccountScopedInput(input, this.#resolver),
            requestId: input.requestId ?? createMutationRequestId(),
        };
        const validated = parse(CancelAllOrdersInputSchema, resolved);
        const res = await this.#writeClient.cancelAllOrders(
            removeUndefined(validated),
            toConnectCallOptions(options),
        );
        return parse(CancelAllOrdersResponseSchema, res);
    }

    /**
     * Arms, refreshes, or disables the account dead-man switch, optionally scoped by symbol ID and side. timeoutSec 0 disables it; 10–120 arms it. Generate a new requestId for each deliberate heartbeat, but reuse the same ID when retrying one ambiguous heartbeat.
     */
    async cancelAllAfter(
        input: CancelAllAfterInput,
        options?: PolyesterMutationOptions,
    ): Promise<CancelAllAfterResult> {
        const resolved = {
            ...resolveAccountScopedInput(input, this.#resolver),
            requestId: input.requestId ?? createMutationRequestId(),
        };
        const request = parse(CancelAllAfterInputSchema, resolved);
        const response = await this.#writeClient.cancelAllAfter(
            removeUndefined(request),
            toConnectCallOptions(options),
        );
        return parse(CancelAllAfterResultSchema, response);
    }

    /**
     * Fetches one physical order and a bounded page of lineage executions through its generation.
     * Pass nextPageToken as pageToken with the same orderId to continue; deduplicate transfers by txId.
     * Set includeExecutionHistory to false for state-only polling, omitting limit and pageToken.
     * Execution pages are eventually consistent and need not reconcile with cumulative order quantities.
     * Returns null when the requested order is not found.
     */
    async getDetails(
        input: v.InferInput<typeof GetOrderDetailsInputSchema>,
        options?: PolyesterRequestOptions,
    ): Promise<OrderDetails | null> {
        await this.#scales.ready();
        const resolved = resolveAccountScopedInput(input, this.#resolver);
        const validatedInput = parse(GetOrderDetailsInputSchema, resolved);
        let res: ProtoRead.GetOrderResponse;
        try {
            res = await this.#readClient.getOrder(
                removeUndefined(validatedInput),
                toConnectCallOptions(options),
            );
        } catch (error) {
            // Injected transports skip SDK error mapping, so load the detail decoder here.
            await loadErrorDetailDecoders(error);
            if (isOrderNotFoundError(error)) return null;
            throw error;
        }
        if (!res.order) return null;
        return parse(this.#orderDetailsSchema, res);
    }

    /**
     * Subscribes to private order updates on private:spot:orders:{accountId}:proto and emits parsed order records until the returned unsubscribe function is called.
     */
    subscribe(input: SubscribeOrdersInput): () => void {
        const channel = `private:spot:orders:${input.accountId}:proto`;
        return connectReadyGatedProtoChannel(this.#realtime, {
            channel,
            schema: ProtoRead.OrderSchema,
            ready: () => this.#scales.ready(),
            onPublication: (data) => {
                const order = parse(this.#orderSchema, data);
                input.onEvent(order);
            },
            onConnected: () => input.onOpen?.(),
            onDisconnected: () => input.onClose?.(),
            onError: input.onError,
        });
    }
}
